import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../../store";
import {
  Matchs,
  Table,
  BlockedMatchModalParams,
  OccupiedTableModalParams,
  Players,
  BlockingPlayer,
  MatchWithBlockInfo,
  BlockedPlayer,
} from "../../types";
import {
  collection,
  CollectionReference,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  DocumentReference,
  updateDoc,
  addDoc,
  deleteField,
} from "firebase/firestore";
import firestoreInstance from "../../services/firestoreInstance";
import checkIsMatchLaunchable, {
  findMatchesToSetDoublonOnLaunch,
  resolveLaunchAttempt,
} from "./isMatchLaunchable";
import { showNotification } from "../globalNotification/globalNotificationSlice";

export type MatchsState = {
  matchs: Matchs[];
  tables: Table[];
  blockedMatchModalParams: BlockedMatchModalParams | null;
  occupiedTableModalParams: OccupiedTableModalParams | null;
  loading: boolean;
};

const initialState: MatchsState = {
  matchs: [],
  tables: [],
  blockedMatchModalParams: null,
  occupiedTableModalParams: null,
  loading: false,
};

/** Retire récursivement les champs `undefined` (interdits par Firestore). */
function stripUndefinedForFirestore<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedForFirestore(item))
      .filter((item) => item !== undefined) as T;
  }
  if (value instanceof Date) {
    return value;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, fieldValue]) => fieldValue !== undefined)
        .map(([key, fieldValue]) => [key, stripUndefinedForFirestore(fieldValue)])
    ) as T;
  }
  return value;
}

function buildFreedPlayersNotificationMessage(
  freedMatchs: Matchs[],
  freedPlayers: Players[]
): string {
  const uniquePlayers = freedPlayers.filter(
    (player, index, all) =>
      player.id != null &&
      all.findIndex((candidate) => candidate.id === player.id) === index
  );

  if (freedMatchs.length === 1 && uniquePlayers.length === 1) {
    const player = uniquePlayers[0];
    return `${player.surname} ${player.name} est disponible (match libéré).`;
  }

  const matchLabel =
    freedMatchs.length === 1
      ? "1 match libéré"
      : `${freedMatchs.length} matchs libérés`;
  return `${matchLabel} — joueurs disponibles.`;
}

// Fonction utilitaire pour nettoyer un joueur (retirer participation)
function cleanPlayer(player: any) {
  if (!player) return player;
  const { participation, ...rest } = player;
  return rest;
}

const getCurrentTournamentId = (state: RootState): string => {
  const tournamentId = state.tournoi.tournoi?.id;
  if (!tournamentId) {
    throw new Error("Tournoi courant introuvable");
  }
  return tournamentId;
};

const getMatchsCollectionRef = (state: RootState): CollectionReference =>
  collection(
    firestoreInstance,
    "tournois",
    getCurrentTournamentId(state),
    "matchs"
  );

const getMatchDocRef = (
  state: RootState,
  matchId: string
): DocumentReference =>
  doc(
    firestoreInstance,
    "tournois",
    getCurrentTournamentId(state),
    "matchs",
    matchId
  );

export type CreateMatchResult = {
  matchId: string;
  status: NonNullable<Matchs["status"]>;
};

export const createMatch = createAsyncThunk<
  CreateMatchResult,
  { match: Matchs },
  { state: RootState; dispatch: AppDispatch }
>("tournoi/createMatch", async ({ match }, { getState }) => {
  const { isMatchLaunchable, blockedMatchModalParams } = checkIsMatchLaunchable(
    match,
    getState().matchs.matchs
  );
  let status: NonNullable<Matchs["status"]> = match.status ?? "CREE";
  if (!isMatchLaunchable) {
    match.status = "DOUBLON";
    status = "DOUBLON";
    for (const bp of blockedMatchModalParams.blockingPlayers) {
      await updateDoc(getMatchDocRef(getState(), bp.match.id), {
        bloquant: true,
      });
    }
  }

  // Récupérer l'ID du tournoi actuel
  const currentTournamentId = getState().tournoi.tournoi?.id;
  if (!currentTournamentId) {
    throw new Error("Tournoi courant introuvable");
  }

  // Ajouter l'ID du tournoi au match
  match.tournoiId = currentTournamentId;

  match.tableau = getState().tournoi.boards.find(
    (board) => board.id === match.tableauId
  );
  match.tour = getState().tournoi.rounds.find(
    (round) => round.id === match.tourId
  );
  const playersWithRegistrations = getState().player.playersWithRegistrations;
  match.matchsPlayerParticipations = match.matchsPlayerParticipations.map(
    (mpp) => ({
      ...mpp,
      players: cleanPlayer(
        playersWithRegistrations.find((p) => p.id === mpp.playersId)
      ),
    })
  );

  try {
    const docRef = await addDoc(
      getMatchsCollectionRef(getState()),
      stripUndefinedForFirestore(match)
    );
    return { matchId: docRef.id, status };
  } catch (error) {
    console.error("Erreur lors de la création du match :", error);
    throw new Error("Erreur lors de la création du match");
  }
});

// Fonction utilitaire qui recalculera la libération des matchs doublons
const recalculateBlockedMatches = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  /** Match qui vient d'être terminé (peut encore être ENCOURS dans Redux). */
  endedMatchId?: string
) => {
  const state = getState();

  // Obtenir tous les matchs ENCOURS actuels, hors celui qu'on vient de terminer
  const liveMatches = state.matchs.matchs.filter(
    (m) =>
      m.status === "ENCOURS" && (!endedMatchId || m.id !== endedMatchId)
  );

  // Tous les doublons encore présents
  const blockedMatchs = state.matchs.matchs.filter(
    (m) => m.status === "DOUBLON"
  );

  let freedMatchs: Matchs[] = [];
  let freedPlayers: Players[] = [];

  for (const doublon of blockedMatchs) {
    // Vérifier s'il reste un joueur bloquant
    const stillBlocked = doublon.matchsPlayerParticipations.some((mpp) => {
      // ce joueur est-il dans un match ENCOURS différent ?
      return liveMatches.some(
        (liveMatch) =>
          liveMatch.id !== doublon.id &&
          liveMatch.matchsPlayerParticipations.some(
            (liveMpp) => liveMpp.playersId === mpp.playersId
          )
      );
    });

    if (!stillBlocked) {
      // Le doublon peut être libéré
      await updateDoc(getMatchDocRef(state, doublon.id), {
        status: "LIBERE",
        bloquant: false,
      });
      freedMatchs.push(doublon);
      // On ajoute les joueurs de ce doublon comme libérés (optionnel)
      freedPlayers.push(
        ...doublon.matchsPlayerParticipations
          .map((mpp) => mpp.players!)
          .filter(Boolean)
      );
    }
  }

  if (freedMatchs.length > 0) {
    dispatch(
      showNotification({
        message: buildFreedPlayersNotificationMessage(
          freedMatchs,
          freedPlayers
        ),
        severity: "success",
        autoHideDuration: 3000,
      })
    );
  }
};

const matchsSlice = createSlice({
  name: "matchs",
  initialState,
  reducers: {
    setMatchs: (state, action: PayloadAction<Matchs[]>) => {
      state.matchs = action.payload;
      state.loading = false;
    },
    setMatchsLoading: (state) => {
      state.loading = true;
    },
    setTables: (state, action: PayloadAction<Table[]>) => {
      state.tables = action.payload;
    },
    launchBlockedMatchModal: (
      state,
      action: PayloadAction<BlockedMatchModalParams>
    ) => {
      state.blockedMatchModalParams = action.payload;
    },
    closeBlockedMatchModal: (state) => {
      state.blockedMatchModalParams = null;
    },
    launchOccupiedTableModal: (
      state,
      action: PayloadAction<OccupiedTableModalParams>
    ) => {
      state.occupiedTableModalParams = action.payload;
    },
    closeOccupiedTableModal: (state) => {
      state.occupiedTableModalParams = null;
    },
  },
});

export const {
  setMatchs,
  setMatchsLoading,
  setTables,
  launchBlockedMatchModal,
  closeBlockedMatchModal,
  launchOccupiedTableModal,
  closeOccupiedTableModal,
} = matchsSlice.actions;

// 🔥 Listen en temps réel aux matchs
export const listenToMatchs = () => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  dispatch(setMatchsLoading());

  try {
    const state = getState();
    const matchsRef = getMatchsCollectionRef(state);
    const q = query(
      matchsRef,
      where("status", "!=", "TERMINE"),
      orderBy("dateCreation")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const matchs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dateCreation: doc.data().dateCreation?.toDate(),
          dateLancement: doc.data().dateLancement?.toDate(),
          dateFin: doc.data().dateFin?.toDate(),
        })) as Matchs[];
        dispatch(setMatchs(matchs));
      },
      (error) => {
        console.error("Erreur onSnapshot matchs:", error);
        // Évite un spinner infini si la query échoue (index, permissions…)
        dispatch(setMatchs([]));
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("Erreur dans listenToMatchs:", error);
    dispatch(setMatchs([]));
    return () => undefined;
  }
};

// 🔥 Chargement initial des tables
export const fetchTables = () => async (dispatch: AppDispatch) => {
  try {
    const snapshot = await getDocs(collection(firestoreInstance, "table"));

    const tables = snapshot.docs.map((doc) => ({
      ...doc.data(),
      tableId: parseInt(doc.id),
    })) as Table[];
    dispatch(setTables(tables));
  } catch (error) {
    console.error("❌ [DEBUG] Erreur dans fetchTables:", error);
    throw error;
  }
};

// 🔥 Change le statut d'un match (TERMINER, ENCOURS, etc)
export const changeMatchStatus =
  (match: Matchs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (match.status === "ENCOURS") {
      // Passage d'un match ENCOURS à TERMINE
      await updateDoc(getMatchDocRef(getState(), match.id), {
        status: "TERMINE",
        dateFin: new Date(),
      });

      // Recalcul libération des doublons (exclure le match qu'on vient de terminer)
      await recalculateBlockedMatches(dispatch, getState, match.id);
    } else {
      const launchAttempt = resolveLaunchAttempt(
        match,
        getState().matchs.matchs
      );

      if (launchAttempt.type === "player_conflict") {
        for (const bp of launchAttempt.blockedMatchModalParams
          .blockingPlayers) {
          await updateDoc(getMatchDocRef(getState(), bp.match.id), {
            bloquant: true,
          });
        }
        await updateDoc(getMatchDocRef(getState(), match.id), {
          status: "DOUBLON",
        });
        dispatch(
          launchBlockedMatchModal(launchAttempt.blockedMatchModalParams)
        );
      } else if (launchAttempt.type === "table_occupied") {
        dispatch(
          launchOccupiedTableModal({
            blockedTable: launchAttempt.blockedTable,
            blockingMatchs: launchAttempt.blockingMatchs,
          })
        );
      } else {
        await updateDoc(getMatchDocRef(getState(), match.id), {
          status: "ENCOURS",
          dateLancement: new Date(),
          bloquant: false,
        });

        const state = getState();
        const matchsToBlock = findMatchesToSetDoublonOnLaunch(
          match,
          state.matchs.matchs
        );

        for (const mToBlock of matchsToBlock) {
          await updateDoc(getMatchDocRef(getState(), mToBlock.id), {
            status: "DOUBLON",
          });
        }

        if (matchsToBlock.length > 0) {
          await updateDoc(getMatchDocRef(getState(), match.id), {
            bloquant: true,
          });

          dispatch(
            launchBlockedMatchModal({
              blockingPlayers: matchsToBlock.map((m) => ({
                player: { id: "", name: "", surname: "" } as Players,
                match: m,
              })),
            })
          );
        }
      }
    }
  };

export const forceMatchEnd = createAsyncThunk<
  void,
  { matchId: string },
  { state: RootState; dispatch: AppDispatch }
>("matchs/forceMatchEnd", async ({ matchId }, { getState, dispatch }) => {
  const state = getState();
  const match = state.matchs.matchs.find((m) => m.id === matchId);
  const wasEncours = match?.status === "ENCOURS";

  await updateDoc(getMatchDocRef(state, matchId), {
    status: "TERMINE",
    dateFin: new Date(),
  });

  await recalculateBlockedMatches(
    dispatch,
    getState,
    wasEncours ? matchId : undefined
  );
});

// 🔥 Changement de table (null = retirer tableId en base)
export const changeMatchTable =
  ({ matchId, tableId }: { matchId: string; tableId: number | null }) =>
  async (_dispatch: AppDispatch, getState: () => RootState) => {
    await updateDoc(getMatchDocRef(getState(), matchId), {
      tableId: tableId == null ? deleteField() : tableId,
    });
  };

// 🔥 Selectors
export const getMatchs = (state: RootState) => state.matchs.matchs;
export const getTables = (state: RootState) => state.matchs.tables;
export const getMatchsLoading = (state: RootState) => state.matchs.loading;
export const getBlockedMatchModalParams = (state: RootState) =>
  state.matchs.blockedMatchModalParams;
export const getOccupiedTableModalParams = (state: RootState) =>
  state.matchs.occupiedTableModalParams;

const getLiveMatchs = (state: RootState) =>
  state.matchs.matchs.filter((match) => match.status === "ENCOURS");

const getAllTables = (state: RootState) => state.matchs.tables;
const getRoomId = (_state: RootState, roomId: number) => roomId;
export const getTablesWithMatchsGroupedByRows = createSelector(
  [getLiveMatchs, getRoomId, getAllTables],
  (matchs, roomId, tables) => {
    // Filtrer uniquement les tables définies et valides avec un roomId
    const tablesFromRoom = tables.filter(
      (table): table is Table =>
        table !== undefined &&
        table !== null &&
        typeof table.roomId !== "undefined" &&
        table.roomId === roomId
    );

    const tablesWithMatchs = tablesFromRoom.map((table) => {
      const matchsOnTable = matchs.filter(
        (match) => match.tableId === table.tableId
      );
      return { ...table, matchs: matchsOnTable };
    });

    const tablesGroupedByRows: { [row: number]: Table[] } = {};
    tablesWithMatchs.forEach((table) => {
      // On vérifie que row est défini avant de l'utiliser
      if (table.row !== undefined && table.row !== null) {
        if (!tablesGroupedByRows[table.row]) {
          tablesGroupedByRows[table.row] = [];
        }
        tablesGroupedByRows[table.row].push(table);
      }
    });

    // Tri par colonne dans chaque rangée
    Object.values(tablesGroupedByRows).forEach((rowTables) => {
      rowTables.sort((a, b) => (a.column > b.column ? 1 : -1));
    });

    return tablesGroupedByRows;
  }
);

/** Salles actives (id + nom alignés — même ordre). */
export const getAvailableRoomsInfo = createSelector(
  [getLiveMatchs, getAllTables],
  (matchs, tables) => {
    const byId = new Map<number, string>();
    for (const match of matchs) {
      const table = tables.find((t) => t.tableId === match.tableId);
      if (!table || typeof table.roomId !== "number") continue;
      if (!byId.has(table.roomId)) {
        byId.set(
          table.roomId,
          table.roomName?.trim() || `Salle ${table.roomId}`
        );
      }
    }
    if (byId.size === 0) {
      return [{ id: 1, name: "Salle 1" }];
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.id - b.id);
  }
);

export const getAvailableRooms = createSelector(
  [getAvailableRoomsInfo],
  (rooms) => rooms.map((r) => r.id)
);

export const getAvailableRoomNames = createSelector(
  [getAvailableRoomsInfo],
  (rooms) => rooms.map((r) => r.name)
);

export const getPlayersWithCurrentMatchs = (state: RootState): Players[] => {
  const players: Players[] = [];

  state.matchs.matchs
    .filter((match) => match.status === "ENCOURS")
    .forEach((match) => {
      (match.matchsPlayerParticipations ?? []).forEach((mpp) => {
        if (mpp.players) {
          const matchPlayerParticipation = { ...mpp, match };
          const player = {
            ...mpp.players,
            matchPlayerParticipation: [matchPlayerParticipation],
          };
          players.push(player);
        }
      });
    });

  return players.sort((p1, p2) => p1.name.localeCompare(p2.name));
};

export const selectMatchesWithBlockInfo = createSelector(
  (state: RootState) => state.matchs.matchs,
  (matches): MatchWithBlockInfo[] => {
    // Filtrer les matchs non terminés pour optimiser
    const activeMatches = matches.filter((m) => m.status !== "TERMINE");

    return activeMatches.map((match) => {
      let blockedByPlayers: BlockingPlayer[] = [];
      let blockedPlayers: BlockedPlayer[] = [];

      if (match.status === "DOUBLON") {
        // Trouver les matchs ENCOURS qui bloquent ce match DOUBLON
        blockedByPlayers = activeMatches
          .filter((m) => m.status === "ENCOURS" && m.bloquant)
          .flatMap((m) => {
            // Récupérer les joueurs communs aux deux matchs
            const blockingPlayerIds = new Set(
              m.matchsPlayerParticipations.map((mpp) => mpp.playersId)
            );
            const isBlocking = match.matchsPlayerParticipations.some((mp) =>
              blockingPlayerIds.has(mp.playersId)
            );

            if (!isBlocking) return [];
            // Ne garder que les joueurs communs (bloquants)
            return match.matchsPlayerParticipations
              .filter((mpp) => blockingPlayerIds.has(mpp.playersId))
              .map((mpp) => ({
                player: mpp.players!,
                match: m,
              }));
          });
      } else if (match.status === "ENCOURS" && match.bloquant) {
        // Trouver les matchs bloqués par ce match ENCOURS
        const blockedMatches = activeMatches.filter((m) => {
          if (m.id === match.id) return false;
          if (m.status === "TERMINE") return false;
          if (m.status === "ENCOURS") return false;

          // Vérifie s'il y a joueur commun (joueur bloqué)
          return m.matchsPlayerParticipations.some((mpp) =>
            match.matchsPlayerParticipations.some(
              (mp) => mp.playersId === mpp.playersId
            )
          );
        });

        // Pour chacun, remonter uniquement les joueurs bloqués communs aux deux matchs
        blockedPlayers = blockedMatches.flatMap((bm) => {
          const blockingPlayerIds = new Set(
            match.matchsPlayerParticipations.map((mpp) => mpp.playersId)
          );

          return bm.matchsPlayerParticipations
            .filter((mpp) => blockingPlayerIds.has(mpp.playersId))
            .map((mpp) => ({
              player: mpp.players!,
              match: bm,
            }));
        });
      }

      return {
        ...match,
        blockedByPlayers:
          blockedByPlayers.length > 0 ? blockedByPlayers : undefined,
        blockedPlayers: blockedPlayers.length > 0 ? blockedPlayers : undefined,
      };
    });
  }
);

export default matchsSlice.reducer;
